import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { ArrowLeft, Upload, MapPin, Euro, Clock, Calendar, Star, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterEquipment = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [equipmentData, setEquipmentData] = useState({
    category: '',
    title: '',
    description: '',
    address: '',
    city: '',
    postal_code: '',
    hourly_rate: '',
    half_day_rate: '',
    daily_rate: '',
    weekly_rate: '',
    specifications: {},
    images: [],
    availability: 'always'
  });

  // Login Dialog Component
  const LoginDialog = ({ isOpen, onOpenChange, onLogin }) => {
    const [loginData, setLoginData] = useState({
      email: '',
      password: ''
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        const response = await axios.post(`${API}/auth/login`, loginData);
        alert('Login effettuato con successo!');
        onLogin(response.data);
        onOpenChange(false);
      } catch (error) {
        alert('Errore nel login: ' + (error.response?.data?.detail || 'Credenziali non valide'));
      }
    };

    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Accedi a quandomiserve.it</DialogTitle>
            <DialogDescription>
              Inserisci le tue credenziali per accedere
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                placeholder="nome@esempio.com"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                placeholder="Password"
                required
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Annulla
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700">
                Accedi
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  // Register Dialog Component
  const RegisterDialog = ({ isOpen, onOpenChange, onRegister }) => {
    const [registerData, setRegisterData] = useState({
      email: '',
      password: '',
      user_type: 'private',
      first_name: '',
      last_name: '',
      phone: '',
      address: '',
      city: '',
      postal_code: '',
      company_name: '',
      vat_number: '',
      unique_code: '',
      pec_email: '',
      legal_representative_id: null,
      signed_contract: null
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      // Validate required documents
      if (!registerData.signed_contract) {
        alert('È obbligatorio caricare il contratto firmato con accettazione dei termini.');
        return;
      }
      
      if (registerData.user_type === 'business' && !registerData.legal_representative_id) {
        alert('È obbligatorio caricare il documento di identità del legale rappresentante per le aziende.');
        return;
      }
      
      try {
        // For now, just simulate success since backend doesn't handle file uploads yet
        alert('Registrazione completata con successo! I documenti caricati saranno verificati dal nostro team entro 48 ore.');
        onRegister({ user_id: 'mock_user', email: registerData.email, first_name: registerData.first_name });
        onOpenChange(false);
      } catch (error) {
        alert('Errore nella registrazione: ' + (error.response?.data?.detail || 'Errore sconosciuto'));
      }
    };

    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registrati su quandomiserve.it</DialogTitle>
            <DialogDescription>
              Crea il tuo account per iniziare a mettere a noleggio la tua attrezzatura
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user_type">Tipo di Account</Label>
              <Select value={registerData.user_type} onValueChange={(value) => setRegisterData({...registerData, user_type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Privato</SelectItem>
                  <SelectItem value="business">Azienda</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Nome</Label>
                <Input
                  id="first_name"
                  value={registerData.first_name}
                  onChange={(e) => setRegisterData({...registerData, first_name: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Cognome</Label>
                <Input
                  id="last_name"
                  value={registerData.last_name}
                  onChange={(e) => setRegisterData({...registerData, last_name: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={registerData.email}
                onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                placeholder="nome@esempio.com"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={registerData.password}
                onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                placeholder="Password"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefono</Label>
              <Input
                id="phone"
                value={registerData.phone}
                onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                placeholder="+39 123 456 7890"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="address">Indirizzo</Label>
                <Input
                  id="address"
                  value={registerData.address}
                  onChange={(e) => setRegisterData({...registerData, address: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Città</Label>
                <Input
                  id="city"
                  value={registerData.city}
                  onChange={(e) => setRegisterData({...registerData, city: e.target.value})}
                  placeholder="Milano, Brescia, Bergamo, Monza..."
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="postal_code">CAP</Label>
              <Input
                id="postal_code"
                value={registerData.postal_code}
                onChange={(e) => setRegisterData({...registerData, postal_code: e.target.value})}
                required
              />
            </div>

            {registerData.user_type === 'business' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="company_name">Nome Azienda</Label>
                  <Input
                    id="company_name"
                    value={registerData.company_name}
                    onChange={(e) => setRegisterData({...registerData, company_name: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vat_number">Partita IVA</Label>
                  <Input
                    id="vat_number"
                    value={registerData.vat_number}
                    onChange={(e) => setRegisterData({...registerData, vat_number: e.target.value})}
                    placeholder="IT12345678901"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="unique_code">Codice Univoco</Label>
                  <Input
                    id="unique_code"
                    value={registerData.unique_code}
                    onChange={(e) => setRegisterData({...registerData, unique_code: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pec_email">PEC</Label>
                  <Input
                    id="pec_email"
                    type="email"
                    value={registerData.pec_email}
                    onChange={(e) => setRegisterData({...registerData, pec_email: e.target.value})}
                    placeholder="azienda@pec.example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="legal_representative_id">Documento Identità Legale Rappresentante *</Label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4">
                    <input
                      id="legal_representative_id"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setRegisterData({...registerData, legal_representative_id: e.target.files[0]})}
                      className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      Carica documento di identità del legale rappresentante (PDF, JPG, PNG - max 5MB)
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Contratto Firmato - per tutti gli utenti */}
            <div className="space-y-2 pt-4 border-t border-slate-200">
              <Label htmlFor="signed_contract">Contratto Firmato e Accettazione Termini *</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4">
                <input
                  id="signed_contract"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setRegisterData({...registerData, signed_contract: e.target.files[0]})}
                  className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                  required
                />
                <p className="text-xs text-slate-500 mt-2">
                  Carica il contratto firmato con accettazione dei termini e condizioni d'uso, responsabilità e regolamenti (PDF - max 10MB)
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                <p className="text-xs text-blue-800">
                  📋 <strong>Importante:</strong> Il contratto deve includere l'accettazione di termini e condizioni d'uso dei dati, 
                  responsabilità civili e penali, e tutti i regolamenti previsti dalla piattaforma quandomiserve.it.
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Importante:</strong> Tutti i campi contrassegnati con * sono obbligatori da compilare. 
                Solo dopo la nostra verifica e conferma dei dati forniti sarà possibile procedere alla registrazione 
                delle attrezzature sulla piattaforma quandomiserve.it.
              </p>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Annulla
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700">
                Registrati
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  // Show login/register prompt if user is not authenticated
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                size="sm"
                className="text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Torna alla Home
              </Button>
              <h1 className="text-2xl font-bold">
                <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                  quandomiserve.it
                </span>
              </h1>
            </div>
          </div>
        </header>

        {/* Authentication Required Section */}
        <div className="max-w-2xl mx-auto px-6 py-20">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                <Upload className="h-10 w-10 text-orange-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900">
                Registra la Tua Attrezzatura
              </h1>
              <p className="text-lg text-slate-600 max-w-md mx-auto">
                Per registrare la tua attrezzatura e iniziare a guadagnare, devi prima creare un account su quandomiserve.it e ricevere la nostra conferma.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 space-y-6">
              <h2 className="text-xl font-semibold text-slate-900">Accedi o Registrati per Continuare</h2>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => setLoginDialogOpen(true)}
                  variant="ghost" 
                  size="lg"
                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-8 py-3"
                >
                  Accedi
                </Button>
                <Button 
                  onClick={() => setRegisterDialogOpen(true)}
                  size="lg"
                  className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-8 py-3"
                >
                  Registrati
                </Button>
              </div>

              <div className="border-t border-slate-200 pt-6">
                <div className="space-y-3 text-sm text-slate-600">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Registrazione gratuita e veloce</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Verifica dati entro 24-48 ore</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Solo dopo la conferma puoi registrare attrezzature</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dialogs */}
        <LoginDialog 
          isOpen={loginDialogOpen}
          onOpenChange={setLoginDialogOpen}
          onLogin={(userData) => {
            setCurrentUser(userData);
            setLoginDialogOpen(false);
          }}
        />
        <RegisterDialog 
          isOpen={registerDialogOpen}
          onOpenChange={setRegisterDialogOpen}
          onRegister={(userData) => {
            setCurrentUser(userData);
            setRegisterDialogOpen(false);
          }}
        />
      </div>
    );
  }

  const categories = [
    { id: 'piattaforme_aeree', name: 'PIATTAFORME AEREE', description: 'Piattaforme elevatrici, cestelli, boom lift' },
    { id: 'sollevamento', name: 'SOLLEVAMENTO', description: 'Gru mobili, paranchi, argani, montacarichi' },
    { id: 'gru_edili', name: 'GRU EDILI', description: 'Gru a torre, gru automontanti, gru per cantiere' },
    { id: 'trabattelli', name: 'TRABATTELLI', description: 'Trabattelli, ponteggi mobili, scale' },
    { id: 'autocarri_furgoni', name: 'AUTOCARRI E FURGONI', description: 'Autocarri, furgoni, veicoli da lavoro' },
    { id: 'movimento_terra', name: 'MOVIMENTO TERRA', description: 'Escavatori, pale, ruspe, bulldozer' },
    { id: 'compattazione', name: 'COMPATTAZIONE E VIBRAZIONE', description: 'Rulli compattatori, piastre vibranti' },
    { id: 'edilizia', name: 'EDILIZIA', description: 'Betoniere, miscelatori, pompe per calcestruzzo' },
    { id: 'attrezzature_cantiere', name: 'ATTREZZATURE DA CANTIERE', description: 'Generatori, compressori, utensili vari' },
    { id: 'container', name: 'CONTAINER', description: 'Container da cantiere, depositi, uffici mobili' },
    { id: 'bagni_mobili', name: 'BAGNI MOBILI', description: 'Bagni chimici, servizi igienici mobili' },
    { id: 'idraulica_pompe', name: 'IDRAULICA POMPE', description: 'Pompe idrauliche, elettropompe, sistemi di drenaggio' },
    { id: 'energia_illuminazione', name: 'ENERGIA E ILLUMINAZIONE', description: 'Generatori, torri faro, sistemi di illuminazione' },
    { id: 'giardinaggio', name: 'GIARDINAGGIO', description: 'Tosaerba, decespugliatori, attrezzature per verde' }
  ];

  const handleCategorySelect = (category) => {
    setEquipmentData({ ...equipmentData, category });
    setCurrentStep(2);
  };

  const handleInputChange = (field, value) => {
    setEquipmentData({ ...equipmentData, [field]: value });
  };

  const handleSubmit = async () => {
    // Mock submission
    alert('Attrezzatura registrata con successo! Sarà verificata dal nostro team entro 24 ore.');
    navigate('/');
  };

  const handleLogin = (userData) => {
    setCurrentUser(userData);
    setLoginDialogOpen(false);
  };

  const handleRegister = (userData) => {
    setCurrentUser(userData);
    setRegisterDialogOpen(false);
  };

  const renderStep1 = () => (
    <div className="space-y-8">
      <div className="text-center space-y-4">
        <h2 className="text-3xl font-bold text-slate-900">
          Che tipo di attrezzatura vuoi mettere a noleggio?
        </h2>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Scegli la categoria che meglio descrive la tua attrezzatura. Potrai aggiungere più dettagli nel passaggio successivo.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((category) => (
          <Card 
            key={category.id}
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-1 border-2 hover:border-orange-500"
            onClick={() => handleCategorySelect(category.id)}
          >
            <CardContent className="p-6 text-center space-y-4">
              <div className="space-y-2">
                <h3 className="font-semibold text-slate-900">{category.name}</h3>
                <p className="text-sm text-slate-600">{category.description}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center space-y-4">
        <Badge variant="outline" className="mb-4">
          {categories.find(c => c.id === equipmentData.category)?.name}
        </Badge>
        <h2 className="text-3xl font-bold text-slate-900">
          Descrivi la tua attrezzatura
        </h2>
        <p className="text-lg text-slate-600">
          Fornisci le informazioni necessarie per aiutare i clienti a trovare e scegliere la tua attrezzatura.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <span>📝</span>
                <span>Informazioni Base</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Nome dell'attrezzatura *</Label>
                <Input
                  id="title"
                  placeholder="es. Escavatore Caterpillar 320D"
                  value={equipmentData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descrizione dettagliata *</Label>
                <Textarea
                  id="description"
                  rows={4}
                  placeholder="Descrivi le caratteristiche, le condizioni, gli accessori inclusi..."
                  value={equipmentData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <MapPin className="h-5 w-5" />
                <span>Posizione</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address">Indirizzo *</Label>
                <Input
                  id="address"
                  placeholder="Via, numero civico"
                  value={equipmentData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">Città *</Label>
                  <Input
                    id="city"
                    placeholder="Milano, Brescia..."
                    value={equipmentData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">CAP *</Label>
                  <Input
                    id="postal_code"
                    placeholder="20100"
                    value={equipmentData.postal_code}
                    onChange={(e) => handleInputChange('postal_code', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Euro className="h-5 w-5" />
                <span>Prezzi di Noleggio</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="hourly_rate">Tariffa oraria (€) *</Label>
                <Input
                  id="hourly_rate"
                  type="number"
                  placeholder="25.00"
                  value={equipmentData.hourly_rate}
                  onChange={(e) => handleInputChange('hourly_rate', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="half_day_rate">Tariffa mezza giornata (€) *</Label>
                <Input
                  id="half_day_rate"
                  type="number"
                  placeholder="100.00"
                  value={equipmentData.half_day_rate}
                  onChange={(e) => handleInputChange('half_day_rate', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="daily_rate">Tariffa giornaliera (€) *</Label>
                <Input
                  id="daily_rate"
                  type="number"
                  placeholder="180.00"
                  value={equipmentData.daily_rate}
                  onChange={(e) => handleInputChange('daily_rate', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="weekly_rate">Tariffa settimanale (€)</Label>
                <Input
                  id="weekly_rate"
                  type="number"
                  placeholder="1000.00"
                  value={equipmentData.weekly_rate}
                  onChange={(e) => handleInputChange('weekly_rate', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Upload className="h-5 w-5" />
                <span>Foto dell'attrezzatura</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                <Upload className="h-8 w-8 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 mb-2">Carica le foto della tua attrezzatura</p>
                <p className="text-sm text-slate-500">PNG, JPG fino a 10MB ciascuna</p>
                <Button variant="outline" className="mt-4">
                  Seleziona File
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="flex justify-between pt-8 border-t">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(1)}
          className="flex items-center space-x-2"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Indietro</span>
        </Button>
        
        <Button
          onClick={handleSubmit}
          className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-8"
          disabled={!equipmentData.title || !equipmentData.description || !equipmentData.address}
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          Pubblica Annuncio
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna alla Home
            </Button>
            <h1 className="text-2xl font-bold">
              <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                quandomiserve.it
              </span>
            </h1>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-8">
            <div className={`flex items-center space-x-2 ${currentStep >= 1 ? 'text-orange-600' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-orange-600 text-white' : 'bg-slate-200'}`}>
                1
              </div>
              <span className="font-medium">Categoria</span>
            </div>
            
            <div className={`flex-1 h-0.5 ${currentStep >= 2 ? 'bg-orange-600' : 'bg-slate-200'}`}></div>
            
            <div className={`flex items-center space-x-2 ${currentStep >= 2 ? 'text-orange-600' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-orange-600 text-white' : 'bg-slate-200'}`}>
                2
              </div>
              <span className="font-medium">Dettagli</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
      </main>

      {/* Benefits Section */}
      {currentStep === 1 && (
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Perché scegliere quandomiserve.it?
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                  <Euro className="h-8 w-8 text-orange-600" />
                </div>
                <h4 className="font-semibold text-slate-900">Guadagna di più</h4>
                <p className="text-slate-600">Metti a reddito le tue attrezzature inutilizzate e genera una fonte di guadagno extra</p>
              </div>
              
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                  <Star className="h-8 w-8 text-orange-600" />
                </div>
                <h4 className="font-semibold text-slate-900">Clienti verificati</h4>
                <p className="text-slate-600">Tutti i noleggiatori sono verificati e assicurati per la tua tranquillità</p>
              </div>
              
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                  <Calendar className="h-8 w-8 text-orange-600" />
                </div>
                <h4 className="font-semibold text-slate-900">Gestione semplice</h4>
                <p className="text-slate-600">Controlli tu quando e come noleggiare le tue attrezzature</p>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default RegisterEquipment;